/**
 * Command log grid for a single site's outbound command history.
 *
 * Shows commands issued to the site's controller (relay, schedule, dim, …)
 * with status, user, and timestamp. Staff users can click any row to see the
 * raw command payload JSON. Supports filtering by command type, status, and
 * date range; CSV export; and infinite-scroll loading.
 *
 * Columns, sort, and column visibility come from the
 * `sites/{id}/unit/commands` grid-spec descriptor. All rendering overrides
 * (timezone-aware timestamp, translated command names, status chips,
 * staff-gated error text) live in fieldOverrides supplied here.
 */

import { FilterList } from '@mui/icons-material';
import Check from '@mui/icons-material/Check';
import {
  Box,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { endOfDay, format, startOfDay, subDays } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import JSONPretty from 'react-json-pretty';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import CommandTypeSelect from '@sophocles/components/CommandTypeSelect';
import ResponsiveDialog from '@sophocles/components/ResponsiveDialog';
import TimeRangeSelector from '@sophocles/components/TimeRangeSelector';
import UserCell from '@sophocles/components/UserCell';
import { EntityGrid } from '@sophocles/data-grids';
import GridStatusCell from '@sophocles/data-grids/components/cells/GridStatusCell';
import { toolbarChipSx } from '@sophocles/data-grids/components/toolbar';
import useGridRefreshRef from '@sophocles/data-grids/datasource/useGridRefreshRef';
import { selectSiteCommandLogRevision } from '@sophocles/features/entities/site';
import {
  adminCommandMap,
  userCommandMap,
  getCommandDisplayName,
  getCommandKeyFromDisplayName,
} from '@sophocles/utils/commandUtils';
import { formatDateTime, getDateTimeFormat } from '@sophocles/utils/formatUtils';
import { formatInZone, formatInZoneWithOffset } from '@sophocles/utils/zonedDateTime';

const RELAY_EVENT_COMMANDS = ['RELAY_ON', 'RELAY_OFF', 'REBOOT_RELAY'];

/**
 * Map a command filter displayName to a list of command keys.
 * Returns null when no filter is active (show all).
 */
function getCommandKeysForFilter(filterValue, isStaff) {
  if (filterValue == null) return null;
  const commandMap = isStaff ? adminCommandMap : userCommandMap;
  const byDisplay = {};
  for (const [key, { displayName }] of Object.entries(commandMap)) {
    if (!byDisplay[displayName]) byDisplay[displayName] = [];
    byDisplay[displayName].push(key);
  }
  return byDisplay[filterValue] || null;
}

function CommandLogGrid({ mode = 'commands', timeZone }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user, isStaff } = useUser();
  const { site_id } = useParams();
  const siteId = site_id ? Number(site_id) : null;
  const commandLogRevision = useSelector(selectSiteCommandLogRevision(siteId));
  const { onGridReady: captureGridReady, refresh: refreshGrid } = useGridRefreshRef();
  const gridReadyRef = useRef(false);
  const pendingGridRefreshRef = useRef(false);
  const previousCommandLogRevisionRef = useRef(undefined);

  const isActivityMode = mode === 'activity';

  // Date range state — default last 30 days
  const [startDate, setStartDate] = useState(() => startOfDay(subDays(new Date(), 29)));
  const [endDate, setEndDate] = useState(() => endOfDay(new Date()));

  // Command type filter (disabled in activity mode)
  const [commandFilter, setCommandFilter] = useState(null);

  // Relay-only toggle (locked on in activity mode)
  const [relayOnly, setRelayOnly] = useState(isActivityMode);

  // Status filter: null | 'PENDING' | 'SENT' | 'FAILED' | 'ACKNOWLEDGED' — matches the
  // serialized/displayed status values (see _build_command_log_row).
  const [statusFilter, setStatusFilter] = useState(null);

  // Status filter popover
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const statusOpen = Boolean(statusAnchorEl);

  // JSON dialog state (staff only)
  const [isDataDialogOpen, setIsDataDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState(null);
  const [dialogInfo, setDialogInfo] = useState({});

  // Grid apiRef — shared with the toolbar's quick-filter wiring without owning
  // useServerDataGrid; passed to EntityGrid via options.gridApiRef so the
  // apiRef is live once the grid mounts.
  const gridApiRef = useGridApiRef();

  const handleGridReady = useCallback(
    api => {
      captureGridReady(api);
      gridReadyRef.current = true;
      if (pendingGridRefreshRef.current) {
        pendingGridRefreshRef.current = false;
        refreshGrid();
      }
    },
    [captureGridReady, refreshGrid],
  );

  useEffect(() => {
    const previousRevision = previousCommandLogRevisionRef.current;
    previousCommandLogRevisionRef.current = commandLogRevision;
    if (previousRevision !== undefined && commandLogRevision !== previousRevision) {
      if (gridReadyRef.current) {
        refreshGrid();
      } else {
        pendingGridRefreshRef.current = true;
      }
    }
  }, [commandLogRevision, refreshGrid]);

  // Resolve the command keys for the active filter (or null for "all")
  const commandKeys = useMemo(() => getCommandKeysForFilter(commandFilter, isStaff), [commandFilter, isStaff]);

  // The descriptor ships error_description as default_hidden; force it
  // visible for staff so failed-command errors read inline in the grid.
  const columnVisibility = useMemo(() => ({ model: { error_description: isStaff } }), [isStaff]);

  // Date range + command filter + relay + status go into extraParams
  const extraParams = useMemo(() => {
    const params = {
      'created_time.gte': format(startDate, 'yyyy-MM-dd'),
      'created_time.lte': format(endDate, 'yyyy-MM-dd'),
    };
    if (commandKeys) params['command.in'] = commandKeys.join(',');
    if (relayOnly) params['command.in'] = RELAY_EVENT_COMMANDS.join(',');
    if (statusFilter) params.status = statusFilter;
    return params;
  }, [startDate, endDate, commandKeys, relayOnly, statusFilter]);

  // Date range change — dataSource recreates automatically
  const handleDateRangeChange = useCallback((start, end) => {
    setStartDate(start);
    setEndDate(end);
  }, []);

  // Command filter change — clears relay toggle (mutually exclusive)
  const handleCommandFilterChange = useCallback(value => {
    setCommandFilter(value);
    setRelayOnly(false);
  }, []);

  // Relay toggle — clears command filter (mutually exclusive)
  const handleRelayToggle = useCallback(() => {
    setRelayOnly(prev => !prev);
    setCommandFilter(null);
  }, []);

  // Status filter handlers
  const handleStatusOpen = useCallback(e => setStatusAnchorEl(e.currentTarget), []);
  const handleStatusClose = useCallback(() => setStatusAnchorEl(null), []);
  const handleStatusSelect = useCallback(
    val => {
      setStatusFilter(val);
      handleStatusClose();
    },
    [handleStatusClose],
  );

  const statusLabels = useMemo(
    () => ({
      PENDING: t('commands.Pending'),
      SENT: t('commands.Sent'),
      FAILED: t('commands.Failed'),
      ACKNOWLEDGED: t('commands.Acknowledged'),
    }),
    [t],
  );

  // Get translated command display name with relay number
  const getTranslatedCommand = useCallback(
    row => {
      const enhancedName = getCommandDisplayName(row);
      const commandKey = getCommandKeyFromDisplayName(enhancedName);
      const translated = t(commandKey);
      return translated === commandKey ? enhancedName : translated;
    },
    [t],
  );

  // Open JSON data dialog (staff only)
  const openDataDialog = useCallback(
    (data, row) => {
      const userName =
        row.user == null || (!row.user.first_name && !row.user.last_name)
          ? t('general.unknown')
          : row.user.last_name
            ? `${row.user.first_name} ${row.user.last_name}`
            : `${row.user.first_name}`;
      const displayTime = formatDateTime(row.created_time, user);
      const status =
        row.status === 'ACKNOWLEDGED'
          ? t('commands.Acknowledged')
          : row.status === 'FAILED'
            ? t('commands.Failed')
            : row.status === 'SENT'
              ? t('commands.Sent')
              : t('commands.Pending');

      setDialogData(data);
      setDialogInfo({
        displayTime,
        userName,
        command: getTranslatedCommand(row),
        status,
        errorDescription: row.error_description,
      });
      setIsDataDialogOpen(true);
    },
    [user, t],
  );

  // Derive status text from a row — backend serializes status as the string
  // "ACKNOWLEDGED" | "SENT" | "FAILED" (see _build_command_log_row).
  const getStatusText = useCallback(
    row => {
      if (row.status === 'ACKNOWLEDGED') return t('commands.Acknowledged');
      if (row.status === 'FAILED') return t('commands.Failed');
      if (row.status === 'SENT') return t('commands.Sent');
      return t('commands.Pending');
    },
    [t],
  );

  // Flatten user to display name
  const getUserName = useCallback(
    userObj => {
      if (userObj == null || (!userObj.first_name && !userObj.last_name)) {
        return t('general.unknown');
      }
      let name = `${userObj.first_name}`;
      if (userObj.last_name) name += ` ${userObj.last_name}`;
      return name;
    },
    [t],
  );

  // Render a colored status chip for a row — backend serializes status as the
  // string "ACKNOWLEDGED" | "SENT" | "FAILED" (see _build_command_log_row).
  const renderStatusChip = useCallback(
    row => {
      if (row.status === 'ACKNOWLEDGED') return <GridStatusCell label={t('commands.Acknowledged')} color="ok" />;
      if (row.status === 'FAILED') return <GridStatusCell label={t('commands.Failed')} color="bad" />;
      if (row.status === 'SENT') return <GridStatusCell label={t('commands.Sent')} color="ok" />;
      return <GridStatusCell label={t('commands.Pending')} color="warning" />;
    },
    [t],
  );

  // The site's time zone, resolved by the parent from the dashboard's site
  // snapshot and passed down — this grid has no reason to read scope itself.
  const structureTimeZone = timeZone;

  // The user's date/time display preference, applied to zoned exports below.
  const dateTimeFormat = useMemo(() => getDateTimeFormat(user), [user]);

  // FE-only display overrides applied on top of the spec's type defaults.
  // These cover cells that need timezone awareness, command translation,
  // status chip rendering, or staff-gated content.
  const fieldOverrides = useMemo(
    () => ({
      created_time: {
        disableColumnMenu: true,
        renderCell: params => {
          if (params.value == null) return '-';
          const displayTime = formatInZone(params.value, structureTimeZone, dateTimeFormat);
          const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const tooltipTime = formatInZone(params.value, browserTimeZone, dateTimeFormat);
          return (
            <Tooltip title={`${t('general.localTime')}: ${tooltipTime}`}>
              <Typography component="span" variant="caption">
                {displayTime}
              </Typography>
            </Tooltip>
          );
        },
      },
      display_name: {
        disableColumnMenu: true,
        valueGetter: (_value, row) => getTranslatedCommand(row),
      },
      user: {
        disableColumnMenu: true,
        // The column is type="reference" in the descriptor, but the row's
        // `user` is a {type,id,first_name,last_name,name} object — render it
        // explicitly (a stringifying valueGetter would feed the reference chip
        // a bare string and surface "undefined"). Staff get a link to the
        // user's profile; a null user / id 0 (system commands) renders plain.
        renderCell: params => <UserCell user={params.row.user} />,
      },
      status: {
        disableColumnMenu: true,
        renderCell: params => renderStatusChip(params.row),
        valueGetter: (_value, row) => getStatusText(row),
      },
      error_description: {
        disableColumnMenu: true,
        renderCell: params =>
          isStaff ? (
            <Typography
              variant="caption"
              sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.35, py: 0.5 }}
            >
              {params.value}
            </Typography>
          ) : null,
      },
    }),
    [t, isStaff, structureTimeZone, dateTimeFormat, getStatusText, renderStatusChip],
  );

  // Mobile card — adapts the listView card body to EntityGrid's renderCard API
  // (receives the row object directly rather than grid cell params).
  const renderMobileCard = useCallback(
    row => {
      const timeDisplay = row.created_time ? formatInZone(row.created_time, structureTimeZone, dateTimeFormat) : '-';
      const commandName = getTranslatedCommand(row);

      return (
        <Box sx={{ py: 1, px: 0.5, width: '100%' }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
            {commandName}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            {timeDisplay}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
            <Chip label={`${t('role.user')}: ${getUserName(row.user)}`} size="small" variant="outlined" />
            {renderStatusChip(row)}
          </Box>
        </Box>
      );
    },
    [structureTimeZone, dateTimeFormat, t, getUserName, renderStatusChip, getTranslatedCommand],
  );

  // Export text for the three columns whose cell is a chip, a link, or a
  // translated command name — the same helpers those cells render through, so
  // the file and the screen cannot say different things about a row.
  const exportOptions = useMemo(
    () => ({
      fileName: isActivityMode ? 'activity-log.csv' : 'command-log.csv',
      columns: {
        display_name: { valueGetter: getTranslatedCommand },
        user: { valueGetter: row => getUserName(row.user) },
        status: { valueGetter: getStatusText },
        created_time: {
          headerName: t('general.unitTimeZone'),
          valueGetter: row => formatInZoneWithOffset(row.created_time, structureTimeZone, dateTimeFormat),
        },
      },
    }),
    [isActivityMode, getTranslatedCommand, getUserName, getStatusText, t, structureTimeZone, dateTimeFormat],
  );

  // Filter controls (left side of toolbar)
  const filterControls = useMemo(
    () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {!isActivityMode && (
          <CommandTypeSelect value={commandFilter} onChange={handleCommandFilterChange} isStaff={isStaff} />
        )}
        <Chip
          icon={<FilterList sx={{ fontSize: 16 }} />}
          label={
            statusFilter ? statusLabels[statusFilter] : t('commands.allStatuses', { defaultValue: 'All Statuses' })
          }
          size="small"
          variant="outlined"
          onClick={handleStatusOpen}
          sx={toolbarChipSx}
        />
        <Popover
          open={statusOpen}
          anchorEl={statusAnchorEl}
          onClose={handleStatusClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: { mt: 0.5, minWidth: 200, maxHeight: 300 } } }}
        >
          <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5, pb: 0.5, color: 'text.secondary' }}>
            {t('commands.filterByStatus', { defaultValue: 'Filter by Status' })}
          </Typography>
          <MenuList dense>
            <MenuItem onClick={() => handleStatusSelect(null)}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                {statusFilter === null ? <Check fontSize="small" /> : null}
              </ListItemIcon>
              <ListItemText>{t('commands.allStatuses', { defaultValue: 'All Statuses' })}</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => handleStatusSelect('PENDING')}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                {statusFilter === 'PENDING' ? <Check fontSize="small" /> : null}
              </ListItemIcon>
              <ListItemText>{t('commands.Pending')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleStatusSelect('SENT')}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                {statusFilter === 'SENT' ? <Check fontSize="small" /> : null}
              </ListItemIcon>
              <ListItemText>{t('commands.Sent')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleStatusSelect('FAILED')}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                {statusFilter === 'FAILED' ? <Check fontSize="small" /> : null}
              </ListItemIcon>
              <ListItemText>{t('commands.Failed')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleStatusSelect('ACKNOWLEDGED')}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                {statusFilter === 'ACKNOWLEDGED' ? <Check fontSize="small" /> : null}
              </ListItemIcon>
              <ListItemText>{t('commands.Acknowledged')}</ListItemText>
            </MenuItem>
          </MenuList>
        </Popover>
        <TimeRangeSelector
          startDate={startDate}
          endDate={endDate}
          onChange={handleDateRangeChange}
          size="sm"
          exclude={['last1h', 'last24h']}
          defaultPreset="last30d"
        />
      </Box>
    ),
    [
      isActivityMode,
      commandFilter,
      handleCommandFilterChange,
      statusFilter,
      statusLabels,
      statusOpen,
      statusAnchorEl,
      handleStatusOpen,
      handleStatusClose,
      handleStatusSelect,
      startDate,
      endDate,
      handleDateRangeChange,
      isStaff,
      t,
    ],
  );

  // Row click opens the JSON dialog for staff users who have a data payload
  const handleRowClick = useCallback(
    params => {
      if (isStaff && params.row?.data) {
        openDataDialog(params.row.data, params.row);
      }
    },
    [isStaff, openDataDialog],
  );

  if (!siteId) return null;

  return (
    <>
      <EntityGrid
        basePath={`sites/${siteId}/unit/commands/log`}
        fieldOverrides={fieldOverrides}
        extraParams={extraParams}
        options={{
          showSearch: false,
          infiniteScroll: true,
          toolbarContent: filterControls,
          export: exportOptions,
          onRowClick: handleRowClick,
          onGridReady: handleGridReady,
          mobile: { renderCard: renderMobileCard },
          gridApiRef,
          columnVisibility,
          gridProps: {
            getRowClassName: params => (params.row?.status === 'FAILED' ? 'row--failed' : ''),
            // Auto height lets a wrapped error message grow its row instead of
            // being clipped to the compact single-line height.
            getRowHeight: () => 'auto',
            disableColumnFilter: true,
            disableColumnSelector: true,
            disableRowSelectionOnClick: true,
            density: 'compact',
            hideFooter: true,
            sx: {
              flex: 1,
              minHeight: 0,
              '& .MuiDataGrid-row': { cursor: isStaff ? 'pointer' : 'default' },
              // Matches the zebra stripe's specificity (`.MuiDataGrid-row.row-odd`)
              // so the failed tint wins the same-element cascade on striped rows.
              '& .MuiDataGrid-row.row--failed': {
                bgcolor: alpha(theme.palette.error.main, 0.04),
                '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.08) },
              },
            },
          },
        }}
      />
      <ResponsiveDialog
        title={`${dialogInfo.displayTime || ''} - ${dialogInfo.userName || ''}`}
        open={isDataDialogOpen}
        onClose={() => setIsDataDialogOpen(false)}
      >
        <div>{`${dialogInfo.command || ''} - ${dialogInfo.status || ''}`}</div>
        {isStaff && dialogInfo.errorDescription ? (
          <Typography variant="body2" color="error" sx={{ mt: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {dialogInfo.errorDescription}
          </Typography>
        ) : null}
        <JSONPretty id="json-pretty" json={dialogData} />
      </ResponsiveDialog>
    </>
  );
}

export default CommandLogGrid;
